<template lang="pug">
.error(v-if="!checkRestrictions('ex:shop')")
    Error(:message="null" :code="403")
.return-reasons(v-else-if="activeCms")
    .header
        h2 {{ $t('Return reasons') }}
        el-button(
            type="primary"
            icon="el-icon-fa-plus fas"
            size="small"
            :disabled="!checkRestrictions('settings', 'update')"
            @click="openCreateDialog"
        ) {{ $t('Add reason') }}

    .alert.info(v-if="!loading && !reasons.length")
        p {{ $t('No return reasons configured yet. Add one so customers can pick a reason for their withdrawal.') }}

    table.reasons-table(v-else v-loading="loading")
        thead
            tr
                th.grip-col
                th {{ $t('Key') }}
                th {{ $t('Name') }}
                th {{ $t('Active') }}
                th.actions
        draggable(
            v-model="reasons"
            tag="tbody"
            handle=".grip"
            @end="persistOrder"
        )
            tr.clickable(
                v-for="reason in reasons"
                :key="reason.id"
                @click="openEditDialog(reason)"
            )
                td.grip-col(@click.stop)
                    i.fas.el-icon-fa-grip-vertical.grip
                td
                    code {{ reason.key }}
                td {{ getName(reason) }}
                td
                    el-tag(
                        :type="reason.active ? 'success' : 'info'"
                        size="mini"
                    ) {{ reason.active ? $t('Yes') : $t('No') }}
                td.actions
                    el-button(
                        icon="el-icon-fa-trash-alt far"
                        size="mini"
                        circle
                        type="danger"
                        :disabled="!checkRestrictions('settings', 'update')"
                        @click.stop="confirmDelete(reason)"
                    )

    el-dialog(
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        :close-on-click-modal="false"
        width="540px"
    )
        el-form(:model="dialogForm" label-position="top")
            el-form-item
                el-switch(
                    v-model="dialogForm.active"
                    :active-text="$t('Active')"
                )
            el-form-item(:label="$t('Key')")
                el-input(v-model="dialogForm.key" :placeholder="$t('e.g. wrong-size, damaged, changed-my-mind')")
            el-form-item(:label="$t('Name')")
                el-input(v-model="dialogForm.name")
        .dialog-footer-buttons(slot="footer")
            el-button(@click="dialogVisible = false") {{ $t('Cancel') }}
            el-button(type="primary" :loading="saving" @click="saveDialog") {{ $t('Save') }}
</template>

<script>
import _ from 'lodash';
import draggable from 'vuedraggable';

import { checkRestrictions } from '@/helpers';
import { useSetupStore } from '@/stores';
import Error from '@/components/Error';
import returnReasonApi from '@/extensions/shop/store/api/returnReason';

export default {
    name: 'ReturnReasonsSettings',
    components: { Error, draggable },
    data() {
        return {
            reasons: [],
            loading: false,
            saving: false,
            dialogVisible: false,
            dialogForm: {
                id: null,
                key: '',
                active: true,
                name: '',
            },
        };
    },
    computed: {
        activeCms() {
            return useSetupStore().activeCms;
        },
        shopId() {
            return _.get(this.activeCms, 'shop.id');
        },
        dialogTitle() {
            return this.dialogForm.id
                ? this.$t('Edit return reason')
                : this.$t('Create return reason');
        },
    },
    mounted() {
        this.fetch();
    },
    methods: {
        checkRestrictions,
        getName(row) {
            const i18n = row.i18n;
            if (!i18n) return row.key;
            try {
                const parsed = typeof i18n === 'string' ? JSON.parse(i18n) : i18n;
                return _.get(parsed, this.$i18n.locale)
                    || Object.values(parsed).find(v => v)
                    || row.key;
            } catch (e) {
                return row.key;
            }
        },
        // vuedraggable's @end fires after the v-model array has been
        // reordered; persist only rows whose position actually changed.
        persistOrder() {
            const updates = [];
            this.reasons.forEach((reason, index) => {
                if (reason.position !== index) {
                    reason.position = index;
                    updates.push({
                        id: reason.id,
                        key: reason.key,
                        position: index,
                        active: reason.active,
                        i18n: typeof reason.i18n === 'string' ? reason.i18n : JSON.stringify(reason.i18n || {}),
                    });
                }
            });
            if (!updates.length) return;
            Promise.all(updates.map(p => returnReasonApi.save(p, this.shopId)))
                .catch(err => {
                    console.error(err);
                    this.fetch();
                });
        },
        fetch() {
            if (!this.shopId) return;
            this.loading = true;
            returnReasonApi.fetchReturnReasons(this.shopId)
                .then(({ data }) => {
                    this.reasons = (data || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
                })
                .catch(err => console.error(err))
                .finally(() => { this.loading = false; });
        },
        openCreateDialog() {
            this.dialogForm = {
                id: null,
                key: '',
                active: true,
                name: '',
            };
            this.dialogVisible = true;
        },
        openEditDialog(row) {
            this.dialogForm = {
                id: row.id,
                key: row.key,
                active: !!row.active,
                name: this.getName(row),
            };
            this.dialogVisible = true;
        },
        saveDialog() {
            this.saving = true;
            const i18n = JSON.stringify({
                de: this.dialogForm.name,
                en: this.dialogForm.name,
            });
            const payload = {
                id: this.dialogForm.id,
                key: this.dialogForm.key,
                position: this.dialogForm.id
                    ? (this.reasons.find(r => r.id === this.dialogForm.id)?.position ?? this.reasons.length)
                    : this.reasons.length,
                active: this.dialogForm.active,
                i18n,
            };
            returnReasonApi.save(payload, this.shopId)
                .then(() => {
                    this.dialogVisible = false;
                    this.fetch();
                    this.$notify.success({
                        title: this.$t('Saved'),
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 4000,
                    });
                })
                .catch(err => {
                    console.error(err);
                    this.$notify.error({
                        title: this.$t('Save failed'),
                        message: err.message || '',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 6000,
                    });
                })
                .finally(() => { this.saving = false; });
        },
        confirmDelete(row) {
            this.$confirm(
                this.$t('Delete this return reason?'),
                this.$t('Confirm delete'),
                {
                    confirmButtonText: this.$t('Delete'),
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning',
                }
            )
                .then(() => returnReasonApi.remove(row.id))
                .then(() => this.fetch())
                .catch(() => { /* dismissed */ });
        },
    },
};
</script>

<i18n>
{
    "de": {
        "Return reasons": "Rücksendegründe",
        "Add reason": "Grund hinzufügen",
        "No return reasons configured yet. Add one so customers can pick a reason for their withdrawal.": "Es sind noch keine Rücksendegründe konfiguriert. Fügen Sie einen hinzu, damit Kunden bei der Widerrufsabgabe einen Grund auswählen können.",
        "Key": "Schlüssel",
        "Name": "Name",
        "Active": "Aktiv",
        "Yes": "Ja",
        "No": "Nein",
        "Actions": "Aktionen",
        "Edit return reason": "Rücksendegrund bearbeiten",
        "Create return reason": "Rücksendegrund anlegen",
        "e.g. wrong-size, damaged, changed-my-mind": "z.B. wrong-size, damaged, changed-my-mind",
        "Cancel": "Abbrechen",
        "Save": "Speichern",
        "Saved": "Gespeichert",
        "Save failed": "Speichern fehlgeschlagen",
        "Delete this return reason?": "Diesen Rücksendegrund löschen?",
        "Confirm delete": "Löschen bestätigen",
        "Delete": "Löschen"
    }
}
</i18n>

<style lang="sass" scoped>
.return-reasons
    .header
        display: flex
        align-items: center
        justify-content: space-between
        margin-bottom: 16px
    .alert.info
        padding: 12px 16px
        background: #e3f2fd
        color: #0d47a1
        border-radius: 4px

.reasons-table
    width: 100%
    border-collapse: collapse
    th, td
        padding: 8px 12px
        text-align: left
        border-bottom: 1px solid var(--border-color, #ebeef5)
    th
        font-size: 12px
        font-weight: 600
        color: var(--color-text-secondary, #606266)
        background: var(--background-base, #fafafa)
    tbody tr.clickable
        cursor: pointer
        &:hover
            background: rgba(0, 0, 0, 0.015)
    .grip-col
        width: 32px
        padding-left: 8px
        cursor: default
    .grip
        cursor: grab
        color: var(--color-text-muted, #909399)
        &:active
            cursor: grabbing
    td.actions
        text-align: right
        white-space: nowrap

.error
    display: flex
    justify-content: center
    min-height: 200px

.dialog-footer-buttons
    display: flex
    justify-content: space-between
    width: 100%
</style>
